/**
 * Survey ranking and categorization logic.
 *
 * Assumptions:
 * - IR (incidence rate) is used as a directional qualification probability proxy
 * - currently_open_min as urgency proxy (0 = data unavailable, >0 = actual count)
 * - No personal affinity score yet (future ML feature — weight zeroed)
 * - points_value is mapped from estimated_points by the API layer
 * - ir is mapped from ir_last_block by the API layer (best available IR)
 */
import type { RankedSurvey } from '@swipeopinion/shared';

// ── Survey type derivation ───────────────────────────────────────────────────

export type SurveyTypeInfo = { label: string; key: string };

export function deriveSurveyType(ptsPerMin: number, loi: number): SurveyTypeInfo {
  if (ptsPerMin >= 6 && loi <= 10) return { label: 'Quick Poll', key: 'quick_poll' };
  if (ptsPerMin >= 6 && loi > 10) return { label: 'In-Depth Study', key: 'in_depth' };
  if (ptsPerMin >= 3 && loi <= 10) return { label: 'Flash Survey', key: 'flash' };
  if (ptsPerMin >= 3 && loi > 10) return { label: 'Brand Study', key: 'brand' };
  if (loi <= 5) return { label: 'Micro Survey', key: 'micro' };
  return { label: 'Survey', key: 'survey' };
}

// ── Title pool (format-descriptive, not topic-specific) ──────────────────────

const TITLES_SHORT = [
  'Quick Opinion Poll', 'Flash Consumer Survey', 'Brief Lifestyle Check',
  'Short Research Study', 'Rapid Opinion Survey',
];
const TITLES_MEDIUM = [
  'Consumer Research Study', 'Lifestyle Opinion Survey',
  'Market Research Survey', 'Consumer Habits Study',
  'Opinion Research Panel', 'Consumer Insights Survey',
];
const TITLES_LONG = [
  'In-Depth Research Study', 'Extended Consumer Survey',
  'Comprehensive Opinion Study', 'Detailed Market Research',
  'Full-Length Research Panel',
];

/** Deterministic title from pool based on survey ID and LOI bracket. */
export function generateSurveyTitle(psSurveyId: number | string, loi: number): string {
  const id = typeof psSurveyId === 'string' ? parseInt(psSurveyId, 10) || 0 : psSurveyId;
  const pool = loi <= 7 ? TITLES_SHORT : loi <= 15 ? TITLES_MEDIUM : TITLES_LONG;
  return pool[Math.abs(id) % pool.length];
}

// ── Match language ───────────────────────────────────────────────────────────

export function getMatchLabel(info: {
  unknownQualsCount: number;
  requiresPrescreen: boolean;
  ir: number;
  exchange: string;
}): { text: string; short: string } {
  if (info.unknownQualsCount === 0 && !info.requiresPrescreen) {
    return { text: 'Strong match', short: 'Strong match' };
  }
  if (info.unknownQualsCount > 0 && info.requiresPrescreen) {
    return { text: 'Good match — a few questions first', short: 'Good match' };
  }
  if (info.ir === 0 && info.exchange === 'rfg') {
    return { text: 'Match unknown', short: 'Unknown' };
  }
  return { text: 'Check eligibility', short: 'Check' };
}

/**
 * Format the match-IR badge text. RFG returns ir=0 to mean "unknown", not
 * a literal 0%, so render "—" for RFG surveys with ir=0. All other surveys
 * get the numeric percentage.
 *
 * NOTE: the survey cards no longer surface this number — it implied a
 * precision the buyer-entered IR can't honor (T3). Kept for any non-card
 * diagnostic use; cards use getMatchTier() instead.
 */
export function formatMatchIr(s: { matchIr: number; exchange?: string }): string {
  if (s.exchange === 'rfg' && s.matchIr === 0) return '—';
  return `${s.matchIr}%`;
}

export type MatchTier = { text: string; tone: 'strong' | 'good' | 'try' };

/**
 * Qualitative match label for survey cards (T3). Replaces the false-precision
 * IR percentage with an honest band derived from the directional IR:
 *   >= 70 → Strong match · 40–69 → Good match · below → Worth a try.
 * RFG returns ir=0 to mean "unknown", so those fall to "Worth a try".
 */
export function getMatchTier(matchIr: number, exchange?: string): MatchTier {
  if (exchange === 'rfg' && matchIr === 0) return { text: 'Worth a try', tone: 'try' };
  if (matchIr >= 70) return { text: 'Strong match', tone: 'strong' };
  if (matchIr >= 40) return { text: 'Good match', tone: 'good' };
  return { text: 'Worth a try', tone: 'try' };
}

/**
 * Tailwind classes for the match-tier pill — a graded, DESCENDING scale that
 * reinforces the label ranking with colour:
 *   strong → green (highest) · good → amber (mid) · try → grey (lowest).
 * Accessibility: the text+background darkness descends with rank, so the scale
 * reads as a ranking even in greyscale (luminance: green text #047857 darkest →
 * amber #B45309 → grey #6B7280 lightest). Hue is reinforcement; the label text
 * already disambiguates. Callers apply their own font-weight, so weight isn't
 * varied here (it would collide with the caller's class).
 */
export function matchTierPillClasses(tone: MatchTier['tone']): string {
  if (tone === 'strong') return 'text-[#047857] bg-[#D1FAE5]'; // green — highest
  if (tone === 'good') return 'text-[#B45309] bg-[#FEF3C7]';   // amber — mid
  return 'text-[#6B7280] bg-[#F3F4F6]';                         // grey — lowest
}

// ── Generic checks ───────────────────────────────────────────────────────────

const GENERIC_RE = /exciting new|ad-hoc|share your|give us/i;
const GENERIC_TITLES = new Set([
  'share your opinion', 'exciting new survey', 'survey',
  'give us your opinion', 'ad-hoc survey',
]);

export function isGenericTitle(title: string): boolean {
  const lower = title.toLowerCase().trim();
  return GENERIC_TITLES.has(lower) || /^survey\s*#?\d*$/i.test(lower) || GENERIC_RE.test(lower);
}

export function isGenericCategory(cat: string | null | undefined): boolean {
  if (!cat) return true;
  return GENERIC_RE.test(cat);
}

// ── Scoring ──────────────────────────────────────────────────────────────────

export interface ScoredSurvey extends RankedSurvey {
  rankScore: number;
  ptsPerMin: number;
  cashValue: string;
  /** Base-rate $ value (struck through on L3+ cards). */
  baseCashValue: string;
  /** True when the member's effective payout exceeds the base rate (L3-L5 with a
   *  real delta) — gates the strikethrough on the base value. */
  hasElevatedBonus: boolean;
  /** The elevated bonus as a DOLLAR increase (effectiveCash − baseCash), derived
   *  from the two point values so it tracks the configured rates. */
  elevatedBonusDelta: number;
  /** Gates the trust-bonus pill: shown only when the bonus is >= $0.05, so we
   *  never render a hollow "+$0.02" badge (the strikethrough still shows). */
  showElevatedBadge: boolean;
  surveyType: SurveyTypeInfo;
  matchLabel: ReturnType<typeof getMatchLabel>;
  matchIr: number;
  displayTitle: string;
  isNew: boolean;
  isFillingFast: boolean;
}

export function getLoi(s: RankedSurvey | ScoredSurvey): number {
  return s.loi ?? s.estimated_length_minutes ?? 1;
}

function getIr(s: RankedSurvey): number {
  return s.ir ?? s.ir_last_block ?? 0;
}

export function scoreSurveys(surveys: RankedSurvey[]): ScoredSurvey[] {
  if (surveys.length === 0) return [];

  const raw = surveys.map((s) => {
    const loi = getLoi(s);
    const ir = getIr(s);
    const ptsPerMin = loi > 0 ? s.points_value / loi : 0;
    const isNew = s.registered_at ? Date.now() - new Date(s.registered_at).getTime() < 20 * 60_000 : false;
    const isRecent = s.registered_at ? Date.now() - new Date(s.registered_at).getTime() < 2 * 60 * 60_000 : false;
    const openMin = s.currently_open_min ?? 999;
    return { survey: s, loi, ir, ptsPerMin, isNew, isRecent, openMin };
  });

  const maxPtsPerMin = Math.max(...raw.map((r) => r.ptsPerMin), 1);
  const maxPts = Math.max(...raw.map((r) => r.survey.points_value), 1);

  return raw.map((r) => {
    const irNorm = r.ir / 100;
    const ppmNorm = r.ptsPerMin / maxPtsPerMin;
    const ptsNorm = r.survey.points_value / maxPts;
    const freshness = r.isNew ? 1.0 : r.isRecent ? 0.5 : 0;
    const urgency = r.openMin > 0 && r.openMin < 10 ? 1.0 : r.openMin > 0 && r.openMin < 25 ? 0.5 : 0;

    const rankScore = irNorm * 0.35 + ppmNorm * 0.30 + ptsNorm * 0.15 + freshness * 0.10 + urgency * 0.10;

    const displayTitle = isGenericTitle(r.survey.title)
      ? generateSurveyTitle(r.survey.id, r.loi)
      : r.survey.title;

    // Elevated-payout strikethrough (L3-L5). base_points uses the BASE rate;
    // points_value uses the member's effective (elevated) rate. They differ only
    // for L3+ members with a real delta — that inequality IS the L3+ gate, so the
    // card needs no trust level. Defensive: equal/missing/zero → no bonus.
    const basePoints = r.survey.base_points ?? r.survey.points_value;
    const hasElevatedBonus = basePoints > 0 && r.survey.points_value > basePoints;
    // Dollar increase (effective − base). Varies per survey (unlike the constant
    // "+14%"), so it pairs naturally with the prominent $ figure.
    const elevatedBonusDelta = hasElevatedBonus ? (r.survey.points_value - basePoints) / 100 : 0;
    // Hide the pill below $0.05 — a "+$0.02" badge is hollow; the strikethrough
    // alone still conveys the bonus on those low-CPI cards.
    const showElevatedBadge = hasElevatedBonus && elevatedBonusDelta >= 0.05;

    return {
      ...r.survey,
      rankScore,
      ptsPerMin: r.ptsPerMin,
      cashValue: (r.survey.points_value / 100).toFixed(2),
      baseCashValue: (basePoints / 100).toFixed(2),
      hasElevatedBonus,
      elevatedBonusDelta,
      showElevatedBadge,
      surveyType: deriveSurveyType(r.ptsPerMin, r.loi),
      matchLabel: getMatchLabel({
        unknownQualsCount: r.survey.unknown_quals_count ?? r.survey.unknownQuals?.length ?? 0,
        requiresPrescreen: r.survey.prescreen ?? false,
        ir: r.ir,
        exchange: r.survey.exchange ?? 'ps',
      }),
      matchIr: r.ir,
      displayTitle,
      isNew: r.isNew,
      isFillingFast: r.openMin > 0 && r.openMin < 20,
    };
  });
}

// ── Diversity reranker ───────────────────────────────────────────────────────

export function diversityRerank(scored: ScoredSurvey[]): ScoredSurvey[] {
  const sorted = [...scored].sort((a, b) => b.rankScore - a.rankScore);
  if (sorted.length <= 6) return sorted;

  const result: ScoredSurvey[] = [];
  const remaining = [...sorted];
  const typeCounts = new Map<string, number>();

  while (result.length < 6 && remaining.length > 0) {
    const idx = remaining.findIndex((s) => (typeCounts.get(s.surveyType.key) ?? 0) < 2);
    const pick = idx >= 0 ? remaining.splice(idx, 1)[0] : remaining.shift()!;
    typeCounts.set(pick.surveyType.key, (typeCounts.get(pick.surveyType.key) ?? 0) + 1);
    result.push(pick);
  }

  function swapIn(candidate: ScoredSurvey | undefined) {
    if (!candidate || result.includes(candidate)) return;
    remaining.push(result[result.length - 1]);
    result[result.length - 1] = candidate;
  }
  if (!result.some((s) => getLoi(s) < 10)) swapIn(remaining.find((s) => getLoi(s) < 10));
  if (!result.some((s) => s.points_value > 50)) swapIn(remaining.find((s) => s.points_value > 50));
  if (!result.some((s) => s.matchIr > 70)) swapIn(remaining.find((s) => s.matchIr > 70));

  remaining.sort((a, b) => b.rankScore - a.rankScore);
  return [...result, ...remaining];
}

// ── Top picks ────────────────────────────────────────────────────────────────

export interface TopPicks {
  bestMatch: ScoredSurvey | null;
  highestPaying: ScoredSurvey | null;
  fastestEarn: ScoredSurvey | null;
}

export function getTopPicks(scored: ScoredSurvey[]): TopPicks {
  if (scored.length === 0) return { bestMatch: null, highestPaying: null, fastestEarn: null };

  const bestMatch = [...scored].sort((a, b) => b.matchIr - a.matchIr)[0] ?? null;
  const highestPaying = [...scored].sort((a, b) => b.points_value - a.points_value)[0] ?? null;
  const eligible = scored.filter((s) => getLoi(s) < 15);
  const fastestEarn = eligible.length > 0 ? eligible.sort((a, b) => b.ptsPerMin - a.ptsPerMin)[0] : null;

  const ids = new Set<string>();
  const dedup = (s: ScoredSurvey | null): ScoredSurvey | null => {
    if (!s || ids.has(s.id)) return null;
    ids.add(s.id);
    return s;
  };
  return { bestMatch: dedup(bestMatch), highestPaying: dedup(highestPaying), fastestEarn: dedup(fastestEarn) };
}

// ── Badge (max one per card, priority ordered) ───────────────────────────────

/**
 * Returns a single badge for a card based on priority:
 * 1. Filling Fast (openMin > 0 && < 10)
 * 2. New (registered < 20 min)
 * 3. High Value (top 20% by pts/min — caller passes threshold)
 * 4. Best Match (top 20% by IR — caller passes threshold)
 * 5. Quick Win (loi <= 7)
 * 6. null (no badge)
 */
export function getPrimaryBadge(
  s: ScoredSurvey,
  ppmThreshold: number = 0,
  irThreshold: number = 0,
): { label: string; color: string; bg: string } | null {
  const openMin = s.currently_open_min ?? 999;
  if (openMin > 0 && openMin < 10) return { label: 'Filling Fast', color: 'text-red-700', bg: 'bg-red-50' };
  if (s.isNew) return { label: 'New', color: 'text-blue-700', bg: 'bg-blue-50' };
  if (ppmThreshold > 0 && s.ptsPerMin >= ppmThreshold) return { label: 'High Value', color: 'text-amber-700', bg: 'bg-amber-50' };
  if (irThreshold > 0 && s.matchIr >= irThreshold) return { label: 'Best Match', color: 'text-purple-700', bg: 'bg-purple-50' };
  if (getLoi(s) <= 7) return { label: 'Quick Win', color: 'text-emerald-700', bg: 'bg-emerald-50' };
  return null;
}

/** Compute top-20% thresholds for badge distribution. */
export function computeBadgeThresholds(surveys: ScoredSurvey[]): { ppm: number; ir: number } {
  if (surveys.length < 5) return { ppm: Infinity, ir: Infinity };
  const sortedPpm = [...surveys].sort((a, b) => b.ptsPerMin - a.ptsPerMin);
  const sortedIr = [...surveys].sort((a, b) => b.matchIr - a.matchIr);
  const idx = Math.max(0, Math.floor(surveys.length * 0.2) - 1);
  return { ppm: sortedPpm[idx].ptsPerMin, ir: sortedIr[idx].matchIr };
}
